import {
  FACILITIES_CONTACT_NAME_MAX, PHONE_NUMBER_PATTERN, isBuildingStaffInviteRole, isValidContactEmail,
  type BuildingStaffInviteRole,
} from '@evi/shared';
import type { BuildingStaffInviteView } from '@/lib/api';

/**
 * The "Invite building staff" form's rules (Warren, 28 Oct 2026, item 5).
 * The Api is the authority (apps/api lib/buildingStaffInvites.ts
 * parseInviteInput); this says which field to fix before sending, with the
 * same words.
 */

export interface InviteDraft { email: string; name: string; role: BuildingStaffInviteRole | '' }

export function checkInviteDraft(d: InviteDraft):
  | { ok: true; body: { email: string; name: string; role: BuildingStaffInviteRole } }
  | { ok: false; error: string } {
  const email = d.email.trim().toLowerCase();
  const name = d.name.trim().replace(/\s+/g, ' ');
  if (!email || !isValidContactEmail(email)) return { ok: false, error: 'Enter their email address.' };
  if (/@(?:[a-z0-9-]+\.)*evinitiative\.com$/i.test(email)) {
    return { ok: false, error: 'EV Initiative staff already see every site. Invite someone at the building.' };
  }
  if (!name) return { ok: false, error: 'Add their name for the invite.' };
  if (name.length > FACILITIES_CONTACT_NAME_MAX) return { ok: false, error: `The name is longer than ${FACILITIES_CONTACT_NAME_MAX} characters.` };
  if (PHONE_NUMBER_PATTERN.test(name)) return { ok: false, error: 'A name cannot hold a phone number.' };
  if (!isBuildingStaffInviteRole(d.role)) return { ok: false, error: 'Choose a role: Property manager, Concierge, Facilities or Other.' };
  return { ok: true, body: { email, name, role: d.role } };
}

function day(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Pending, link works until Nov 4, 2026", "Expired Nov 4, 2026", "Accepted Oct 29, 2026". */
export function inviteStatusLabel(inv: Pick<BuildingStaffInviteView, 'status' | 'expires_at' | 'accepted_at' | 'invited_by_name'>): string {
  const by = inv.invited_by_name ? `, invited by ${inv.invited_by_name}` : '';
  if (inv.status === 'accepted') return `Accepted ${day(inv.accepted_at)}${by}`;
  if (inv.status === 'expired') return `Expired ${day(inv.expires_at)}${by}`;
  return `Pending, link works until ${day(inv.expires_at)}${by}`;
}
